import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@@shared/base/Button';
import { Card } from '@@shared/base/Card';

interface ReceiptCardProps {
  onSendReceipt: (email: string) => void;
  onFinish: () => void;
  isLoading?: boolean;
  error?: string | null;
}

export const ReceiptCard = ({ onSendReceipt, onFinish, isLoading, error }: ReceiptCardProps) => {
  const tPayment = useTranslation('translation', { keyPrefix: 'payment' }).t;
  const [wantsReceipt, setWantsReceipt] = useState(false);
  const [email, setEmail] = useState('');

  const handleSendReceipt = () => {
    onSendReceipt(email);
  };

  const isEmailValid = email.trim().length > 0 && email.includes('@');

  return (
    <Card className="p-8 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
        <Check className="h-12 w-12 text-vk-dark" strokeWidth={3} />
      </div>

      <h2 className="mb-6 font-averta text-xl text-vk-dark">{tPayment('title')}</h2>

      <div className="mb-8 space-y-4 text-vk-dark">
        <p className="text-md font-semibold">{tPayment('subtitle')}</p>
        <p className="text-md leading-relaxed">{tPayment('body')}</p>
      </div>

      <div className="mb-6 rounded-lg bg-gray-100 p-5">
        <h3 className="mb-4 text-left font-semibold text-vk-dark">{tPayment('receiptSectionTitle')}</h3>

        <label className="flex cursor-pointer items-center gap-3">
          <div className="relative">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={wantsReceipt}
              onChange={(e) => setWantsReceipt(e.target.checked)}
            />
            <div className="h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-green-500"></div>
            <div className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"></div>
          </div>
          <span className="text-sm text-vk-dark">{tPayment('receiptCheckboxLabel')}</span>
        </label>

        {wantsReceipt && (
          <>
            <input
              type="email"
              placeholder={tPayment('emailPlaceholder') || undefined}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-4 w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-green-500 focus:outline-none"
            />
            <p className="ml-2 mt-2 text-left text-xs text-vk-dark">{tPayment('emailDisclaimer')}</p>
            {error && <p className="mt-2 text-left text-sm text-red-600">{error}</p>}
          </>
        )}
      </div>

      <Button
        onClick={wantsReceipt ? handleSendReceipt : onFinish}
        className={`w-full ${
          (wantsReceipt && !isEmailValid) || isLoading ? '!hover:bg-gray-400 !cursor-not-allowed !bg-gray-300' : ''
        }`}
        disabled={(wantsReceipt && !isEmailValid) || isLoading}
      >
        {isLoading ? tPayment('sendingButton') : wantsReceipt ? tPayment('sendButton') : tPayment('button')}
      </Button>
    </Card>
  );
};
